import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { TripHelper } from '@lib/TripHelper'
import { Event, Trip } from '@types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DaysFilter from './components/DaysFilter'
import EventList from './components/EventList'
import MapCanvas from './components/MapCanvas'
import { ContentRow, MapWrapper, SidePanel } from './TripMapPage.styles'
import { initialSelectedDays, numberMappedEvents, splitEventsByGeometry } from './utils'

export interface TripMapViewProps {
	trip: Trip
	events: Event[] | undefined
	/** `?day=` (1-based) the itinerary linked here with; absent for the whole trip. */
	dayParam: string | null
	/** The empty state's line under its title — member mode asks for a location, public mode only says there is none. */
	emptyDescription: string
	onBackToItinerary: () => void
}

/**
 * The trip's events on the map: the days filter and the list beside the map,
 * or an empty state when nothing has a location. The same view in both modes —
 * each page supplies the frame, the data, and where "back" leads.
 */
const TripMapView: React.FC<TripMapViewProps> = ({ trip, events, dayParam, emptyDescription, onBackToItinerary }) => {
	const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set())
	const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
	const [hasInitialized, setHasInitialized] = useState(false)

	const dayCount = useMemo(() => {
		const helper = new TripHelper(trip)
		return helper.hasValidDates() ? (trip.dayCount ?? 0) : 0
	}, [trip])

	const split = useMemo(() => splitEventsByGeometry(events), [events])

	const mappedCountByDay = useMemo(() => {
		const counts = new Map<number, number>()
		split.mapped.forEach(m => counts.set(m.dayIndex, (counts.get(m.dayIndex) ?? 0) + 1))
		return counts
	}, [split.mapped])

	// Default to every day that has pins — or to the one day the itinerary
	// linked here with (`?day=`, 1-based); the filter narrows from there.
	useEffect(() => {
		if (hasInitialized || split.mapped.length === 0) return
		setSelectedDays(initialSelectedDays(split.mapped, dayParam))
		setHasInitialized(true)
	}, [split.mapped, hasInitialized, dayParam])

	const filteredMapped = useMemo(
		() => numberMappedEvents(split.mapped.filter(m => selectedDays.has(m.dayIndex))),
		[split.mapped, selectedDays],
	)

	const filteredUnmapped = useMemo(
		() => split.unmapped.filter(u => selectedDays.has(u.dayIndex)),
		[split.unmapped, selectedDays],
	)

	const handleDaysChange = useCallback((next: Set<number>) => {
		setSelectedDays(next)
		setSelectedEventId(null)
	}, [])

	const handleSelect = useCallback((eventId: number) => {
		setSelectedEventId(prev => (prev === eventId ? null : eventId))
	}, [])

	if (split.mapped.length === 0) {
		return (
			<EmptyState art="itinerary" title="No locations on the map yet" description={emptyDescription}>
				<EmptyStateButton onClick={onBackToItinerary}>Back to itinerary</EmptyStateButton>
			</EmptyState>
		)
	}

	return (
		<ContentRow>
			<SidePanel>
				<DaysFilter
					dayCount={dayCount}
					selectedDays={selectedDays}
					mappedCountByDay={mappedCountByDay}
					onChange={handleDaysChange}
				/>
				<EventList
					mapped={filteredMapped}
					unmapped={filteredUnmapped}
					selectedEventId={selectedEventId}
					onSelect={handleSelect}
				/>
			</SidePanel>
			<MapWrapper>
				<MapCanvas events={filteredMapped} selectedEventId={selectedEventId} onMarkerClick={handleSelect} />
			</MapWrapper>
		</ContentRow>
	)
}

export default TripMapView
